import React, { useEffect, useRef } from "react";
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import { motion, AnimatePresence } from "framer-motion";
import testbike from "../images/bikes/1.png";
import bikedealer from '../images/nukeproof-dealers.png';
import ReactPlayer from "react-player/lazy";
import { NavLink, useParams } from "react-router-dom";
import { FaTimes, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import "./bike-example.css"
import BikeExampleLoading from "./bike-example-loader";
import { useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";

import telowhite from "../images/vlastnosti/nukeproof-tělo-white.png";
import teloblack from "../images/vlastnosti/nukeproof-tělo-black.png";
import { getLocalizedText } from "../utils/textLocalization";
import { formatPrice } from "../utils/currencyUtils";
import ExchangeRateTooltip from "../components/ExchangeRateTooltip";
import RozbalovaciNabidka from "../components/RozbalovaciNabidka";
import { getCurrentLanguage } from "../utils/languageSwitcher";
import { getModelEntryData, useEditry } from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';

import specsBackground from "../images/bike-new-sides.png";
import { MdExpandMore } from "react-icons/md";

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu'
};

const parseModelItemData = (item) => {
  const parsed = getModelEntryData(item);
  if (parsed && Object.keys(parsed).length > 0) {
    return parsed;
  }

  if (item && typeof item.data_json === 'string') {
    try {
      return JSON.parse(item.data_json);
    } catch {
      return {};
    }
  }

  return {};
};

const extractOProduktuObrazky = (data, fallbackData) => {
  const urls = [];

  const collect = (source) => {
    if (!source || typeof source !== 'object') {
      return;
    }

    Object.keys(source).forEach((entryKey) => {
      if (entryKey.toLowerCase() !== 'oproduktuobrazky') {
        return;
      }

      const items = source[entryKey];
      if (!Array.isArray(items)) {
        return;
      }

      items.forEach((item) => {
        let url = null;

        if (typeof item === 'string') {
          url = item;
        } else if (item && typeof item === 'object') {
          const imageKey = Object.keys(item).find((key) => key.toLowerCase() === 'obrazek');
          url = imageKey ? item[imageKey] : item.obrazek;
        }

        if (typeof url === 'string' && url.trim() && !urls.includes(url)) {
          urls.push(url.trim());
        }
      });
    });
  };

  collect(data);
  if (urls.length === 0) {
    collect(fallbackData);
  }

  return urls;
};

const legacyVelikostiToLabels = (velikostiData) => {
  // Backward compatibility for older backend shape:
  // velikosti: {xxxs: true, xxs: false, ...}
  if (!velikostiData || typeof velikostiData !== "object" || Array.isArray(velikostiData)) return [];

  const labels = [];
  if (velikostiData.xxxs) labels.push("XXXS");
  if (velikostiData.xxs) labels.push("XXS");
  if (velikostiData.xs) labels.push("XS");
  if (velikostiData.s) labels.push("S");
  if (velikostiData.m) labels.push("M");
  if (velikostiData.l) labels.push("L");
  if (velikostiData.xl) labels.push("XL");
  // Historically used "Ď" for Czech "D".
  if (velikostiData.d) labels.push("Ď");
  return labels;
};

// Specification name translations
const SPEC_TRANSLATIONS = {
  'Rám': { HUN: 'Váz', PL: 'Rama', SK: 'Rám' },
  'Motor': { HUN: 'Motor', PL: 'Silnik', SK: 'Motor' },
  'Baterie': { HUN: 'Akkumulátor', PL: 'Akumulator', SK: 'Batéria' },
  'Tlumič': { HUN: 'Rugóstag', PL: 'Damper', SK: 'Tlmič' },
  'Vidlice': { HUN: 'Villa', PL: 'Widelec', SK: 'Vidlica' },
  'Řazení': { HUN: 'Váltókar', PL: 'Manetki', SK: 'Radenie' },
  'Přesmykač': { HUN: 'Első váltó', PL: 'Przerzutka przednia', SK: 'Prešmykač' },
  'Přehazovačka': { HUN: 'Hátsó váltó', PL: 'Przerzutka tylna', SK: 'Prehadzovačka' },
  'Kliky': { HUN: 'Hajtómű', PL: 'Korba', SK: 'Kľuky' },
  'Kazeta': { HUN: 'Lánckeréksor', PL: 'Kaseta', SK: 'Kazeta' },
  'Brzdy': { HUN: 'Fékek', PL: 'Hamulce', SK: 'Brzdy' },
  'Řidítka': { HUN: 'Kormány', PL: 'Kierownica', SK: 'Riadidlá' },
  'Představec': { HUN: 'Kormányszár', PL: 'Mostek', SK: 'Predstavec' },
  'Sedlovka': { HUN: 'Nyeregszár', PL: 'Sztyca', SK: 'Sedlovka' },
  'Sedlo': { HUN: 'Nyere', PL: 'Siodło', SK: 'Sedlo' },
  'Zapletená kola': { HUN: 'Kerekek', PL: 'Koła', SK: 'Zapletené kolesá' },
  'Pláště': { HUN: 'Gumi', PL: 'Opony', SK: 'Plášte' },
  'Rámová sada': { HUN: 'Vázszett', PL: 'Zestaw ramowy', SK: 'Rámová sada' }
};

const translateSpecName = (czechName) => {
  const locale = getCurrentLanguage();
  if (locale === 'cs-CZ') return czechName; // Czech - no translation needed
  
  const localeMap = {
    'hu-HU': 'HUN',
    'pl-PL': 'PL',
    'sk-SK': 'SK'
  };
  
  const targetLang = localeMap[locale];
  return SPEC_TRANSLATIONS[czechName]?.[targetLang] || czechName;
};

const LOCALE_TO_BUILDER_KEY = {
  'cs-CZ': 'Default',
  'sk-SK': 'sk-SK',
  'pl-PL': 'pl-PL',
  'hu-HU': 'hu-HU'
};

const toDisplayString = (value, locale, fallback = '') => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const localeKey = LOCALE_TO_BUILDER_KEY[locale] || 'Default';
  const localeValue = value[localeKey];
  if (typeof localeValue === 'string' || typeof localeValue === 'number') {
    return String(localeValue);
  }

  if (typeof value.Default === 'string' || typeof value.Default === 'number') {
    return String(value.Default);
  }

  if (typeof value.value === 'string' || typeof value.value === 'number') {
    return String(value.value);
  }

  const firstString = Object.values(value).find((entry) => typeof entry === 'string' || typeof entry === 'number');
  if (firstString !== undefined) {
    return String(firstString);
  }

  return fallback;
};

const normalizeGeometryLetterId = (id) => String(id ?? "").trim().toUpperCase();
const normalizeGeometryName = (name) => String(name ?? "").trim().toLowerCase();

const normalizeSpecEntry = (entry) => ({
  jmenoSpecifikace: entry?.jmenoSpecifikace ?? entry?.jmenospecifikace ?? "",
  hodnotaSpecifikace: entry?.hodnotaSpecifikace ?? entry?.hodnotaspecifikace ?? ""
});

const normalizeSpecCategory = (category) => ({
  kategorie: category?.kategorie,
  cena: category?.cena,
  obrazek: category?.obrazek,
  rozbaleno: category?.rozbaleno === true,
  specifikace: (Array.isArray(category?.specifikace) ? category.specifikace : []).map(normalizeSpecEntry)
});

const normalizeSpecifikaceList = (specifikaceRaw) => {
  if (!specifikaceRaw) return null;

  let specifikace = specifikaceRaw;
  if (typeof specifikace === "string") {
    try {
      specifikace = JSON.parse(specifikace);
    } catch {
      return null;
    }
  }

  if (!Array.isArray(specifikace) || specifikace.length === 0) return null;

  const first = specifikace[0];
  const categories = first?.nazevKategorie ?? first?.nazevkategorie;
  if (!Array.isArray(categories)) return null;

  return {
    nazevKategorie: categories.map(normalizeSpecCategory)
  };
};

const isRozbalovaciNabidkaEnabled = (data) => {
  const flag = data?.rozbalovacinabidka ?? data?.rozbalovaciNabidka ?? data?.rozbalovaci_nabidka;
  return flag === true || flag === "true" || flag === 1 || flag === "1";
};

const normalizeOfferKey = (value) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const COLOR_SPEC_FIELD_NAMES = new Set(["barva", "barvy", "color", "colors", "colour", "colours"]);

const colorDisplayName = (color) => normalizeOfferKey(
  toDisplayString(color?.jmenoBarvy ?? color?.jmenobarvy ?? "", getCurrentLanguage(), "")
);

const splitColorList = (value) => String(value ?? "")
  .split(/[,;/|]|\s+a\s+|\s+and\s+/i)
  .map((part) => normalizeOfferKey(part))
  .filter(Boolean);

const specCategoryName = (spec) => {
  if (typeof spec === "string" || typeof spec === "number") {
    return String(spec);
  }
  return toDisplayString(spec?.kategorie, getCurrentLanguage(), "");
};

const declaredColorNames = (spec) => {
  if (!spec || typeof spec !== "object") {
    return [];
  }

  const entries = Array.isArray(spec.specifikace) ? spec.specifikace : [];
  const names = [];

  entries.forEach((entry) => {
    const fieldName = normalizeOfferKey(entry?.jmenoSpecifikace ?? entry?.jmenospecifikace ?? "");
    if (!COLOR_SPEC_FIELD_NAMES.has(fieldName)) {
      return;
    }
    const rawValue = entry?.hodnotaSpecifikace ?? entry?.hodnotaspecifikace ?? "";
    names.push(...splitColorList(toDisplayString(rawValue, getCurrentLanguage(), "")));
  });

  return names;
};

const extractColorSpecTags = (color) => {
  const raw = color?.specifikace ?? color?.Specifikace;
  if (!raw) return [];

  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((entry) => {
      if (typeof entry === "string" || typeof entry === "number") {
        return normalizeOfferKey(entry);
      }
      if (!entry || typeof entry !== "object") {
        return "";
      }
      return normalizeOfferKey(
        entry.kategorie ?? entry.nazev ?? entry.name ?? entry.value ?? entry.jmeno ?? ""
      );
    })
    .filter(Boolean);
};

const colorMatchesVariantName = (color, specName) => {
  const target = normalizeOfferKey(specName);
  if (!target) return false;

  const tags = extractColorSpecTags(color);
  if (tags.includes(target)) {
    return true;
  }

  const colorName = colorDisplayName(color);
  if (colorName && colorName === target) {
    return true;
  }

  const barvaValue = color?.barva;
  if (
    typeof barvaValue === "string" &&
    !barvaValue.toLowerCase().includes("rgb") &&
    !barvaValue.startsWith("#") &&
    normalizeOfferKey(barvaValue) === target
  ) {
    return true;
  }

  return false;
};

const colorMatchesSpec = (color, spec) => {
  const declared = declaredColorNames(spec);
  const colorName = colorDisplayName(color);
  if (declared.length > 0 && colorName && declared.includes(colorName)) {
    return true;
  }

  return colorMatchesVariantName(color, specCategoryName(spec));
};

const filterColorsForSpec = (colors, specName) => {
  if (!Array.isArray(colors) || colors.length === 0 || !specName) {
    return [];
  }
  return colors.filter((color) => colorMatchesSpec(color, specName));
};

const resolveColorsForSpec = (colors, specName) => {
  const matched = filterColorsForSpec(colors, specName);
  return matched.length > 0 ? matched : (Array.isArray(colors) ? colors : []);
};

const resolveSpecImage = (spec, colors) => {
  if (spec?.obrazek) {
    return spec.obrazek;
  }
  if (colors?.[0]?.obrazek) {
    return colors[0].obrazek;
  }
  return null;
};

const collectGeometryRows = (columns, locale) => {
  const rowMap = new Map();
  const nameToLetterMap = new Map();

  columns.forEach((column) => {
    (Array.isArray(column?.geometrie) ? column.geometrie : []).forEach((entry) => {
      const letterId = normalizeGeometryLetterId(entry?.id);
      const name = toDisplayString(entry?.name, locale, "").trim();
      const normalizedName = normalizeGeometryName(name);
      if (letterId && normalizedName) {
        nameToLetterMap.set(normalizedName, letterId);
      }
    });
  });

  columns.forEach((column) => {
    (Array.isArray(column?.geometrie) ? column.geometrie : []).forEach((entry) => {
      const rawLetterId = normalizeGeometryLetterId(entry?.id);
      const name = toDisplayString(entry?.name, locale, "").trim();
      const normalizedName = normalizeGeometryName(name);
      const letterId = rawLetterId || nameToLetterMap.get(normalizedName) || "";
      if (!letterId && !name) return;

      const key = letterId || name;
      const existing = rowMap.get(key);
      if (!existing) {
        rowMap.set(key, { id: letterId || null, name: name || key });
        return;
      }
      if (letterId && !existing.id) {
        rowMap.set(key, { ...existing, id: letterId });
      }
      if (name && !existing.name) {
        rowMap.set(key, { ...existing, name });
      }
    });
  });

  return Array.from(rowMap.values()).sort((a, b) => {
    if (a.id && b.id) return a.id.localeCompare(b.id);
    if (a.id) return -1;
    if (b.id) return 1;
    return a.name.localeCompare(b.name);
  });
};

const findGeometryEntry = (geometrie, row, locale) => {
  return (Array.isArray(geometrie) ? geometrie : []).find((entry) => {
    const entryId = normalizeGeometryLetterId(entry?.id);
    const entryName = toDisplayString(entry?.name, locale, "").trim();
    if (row.id && entryId) return entryId === row.id;
    return entryName === row.name;
  });
};

function App({theme, setTitleModal, setTextModal, setModal, setImageModal, setTableModal, tableId, bikeData}) {
  const { client } = useEditry();
  const editryReady = isEditryConfigured();
  const currentLocale = getCurrentLanguage();
  const language = LOCALE_TO_CONTENT_LANGUAGE[currentLocale] || 'cs';

  const { id, kategorie } = useParams();
  const [loading, setLoading] = useState(true);
  const [bike, setBike] = useState(null);
  const [currentBikeImage, setCurrentBikeImage] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedSpecIndex, setSelectedSpecIndex] = useState(0);
  const [specDropdownOpen, setSpecDropdownOpen] = useState(false);
  const [size, setSize] = useState("M");
  const [dostupneVelikosti, setDostupneVelikosti] = useState([]);
  const [velikostiDetaily, setVelikostiDetaily] = useState([]);
  const [vlastnosti] = useState(true);
  const [openKategory, setOpenKategory] = useState(-1);
  //const [openCategories, setOpenCategories] = useState({});

  const defaultSpecifikace = {
    nazevKategorie: [
      {
        kategorie: "Rám",
        specifikace: [
          { jmenoSpecifikace: "Materiál", hodnotaSpecifikace: "Carbon" },
          { jmenoSpecifikace: "Velikosti", hodnotaSpecifikace: "S, M, L, XL" }
        ]
      }
    ]
  };

  // Reference pro UI elementy
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const specDropdownRef = useRef(null);

  // Galerie obrázků
  const [galleryImages, setGalleryImages] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [vlastnostiExpanded, setVlastnostiExpanded] = useState(-1);

  useEffect(() => {
    let cancelled = false;

    const hydrateBikeState = (foundBike, availableVelikostiLabels = [], availableVelikostiDetails = [], fallbackData = null) => {
      if (!foundBike || !foundBike.data) {
        return;
      }

      setBike(foundBike);
      window.document.title = `Nukeproof | ${toDisplayString(foundBike.data?.nazev, currentLocale, "Nukeproof kolo")}`;

      if (Array.isArray(availableVelikostiLabels) && availableVelikostiLabels.length > 0) {
        setDostupneVelikosti(availableVelikostiLabels);
        setSize((prev) => (availableVelikostiLabels.includes(prev) ? prev : availableVelikostiLabels[0] || prev));
      } else {
        setDostupneVelikosti([]);
      }
      setVelikostiDetaily(Array.isArray(availableVelikostiDetails) ? availableVelikostiDetails : []);

      if (foundBike.data?.obrazek) {
        setCurrentBikeImage(foundBike.data.obrazek);
      } else {
        setCurrentBikeImage(null);
      }

      setGalleryImages(extractOProduktuObrazky(foundBike.data, fallbackData));

      const normalizedSpecifikace = normalizeSpecifikaceList(foundBike.data?.specifikace);
      if (normalizedSpecifikace?.nazevKategorie) {
        const initialState = {};
        normalizedSpecifikace.nazevKategorie.forEach((category, index) => {
          initialState[index] = category.rozbaleno === true;
        });
        //setOpenCategories(initialState);
      } else {
        //setOpenCategories({});
      }
    };

    const loadModelItemDetail = async (modelId, itemId) => {
      let localizedItem = null;
      let defaultItem = null;

      if (language && language !== 'cs') {
        try {
          const localizedResponse = await client.getPublicModelItemById(modelId, itemId, language);
          localizedItem = localizedResponse?.data || null;
        } catch {
          localizedItem = null;
        }
      }

      try {
        const defaultResponse = await client.getPublicModelItemById(modelId, itemId);
        defaultItem = defaultResponse?.data || null;
      } catch {
        defaultItem = null;
      }

      const primaryItem = localizedItem || defaultItem;
      if (!primaryItem) {
        return null;
      }

      return {
        item: primaryItem,
        fallbackData: defaultItem ? parseModelItemData(defaultItem) : null,
      };
    };

    const resolveVelikostDataFromRefs = async (velikostiField, modelsResponse) => {
      // New backend shape:
      // velikosti: [{ velikost: { id, model, type } }, ...]
      if (!velikostiField) return { labels: [], details: [] };

      if (!Array.isArray(velikostiField)) {
        // Old backend shape: {xxxs: true, ...}
        const labels = legacyVelikostiToLabels(velikostiField);
        return { labels, details: [] };
      }

      const models = modelsResponse?.data || [];
      const velikostiModel = models.find((m) => String(m?.slug || m?.model || "").toLowerCase() === "velikosti");
      if (!velikostiModel) {
        const labels = velikostiField
          .map((ref) => String(ref?.velikost?.id ?? ref?.id ?? "").trim())
          .filter(Boolean);
        return { labels, details: [] };
      }

      let itemsResponse = await client.getPublicModelItems(velikostiModel.id, language);
      let allItems = itemsResponse?.data || [];

      if (!allItems.length) {
        itemsResponse = await client.getPublicModelItems(velikostiModel.id);
        allItems = itemsResponse?.data || [];
      }

      const parsedItems = allItems
        .map((item) => {
          const parsed = parseModelItemData(item);
          return {
            id: String(item?.id ?? ""),
            parsed,
          };
        })
        .filter((x) => x.id);

      const byId = new Map(parsedItems.map((x) => [x.id, x.parsed]));

      // Keep backend order.
      const details = velikostiField
        .map((ref, index) => {
          const id = String(ref?.velikost?.id ?? ref?.id ?? "").trim();
          if (!id) return null;
          const parsed = byId.get(id);
          const displayName = toDisplayString(
            parsed?.nazev ?? parsed?.jmeno ?? parsed?.name ?? parsed?.label,
            currentLocale,
            id
          );
          const podvelikost = toDisplayString(parsed?.podvelikost, currentLocale, "").trim();
          return {
            id,
            displayName,
            podvelikost,
            geometrie: Array.isArray(parsed?.geometrie) ? parsed.geometrie : [],
            sortOrder: index
          };
        })
        .filter(Boolean);

      return {
        labels: details.map((item) => item.displayName),
        details
      };
    };

    const loadBike = async () => {
      setLoading(true);

      const fromProp = Array.isArray(bikeData)
        ? bikeData.find((b) => String(b.id) === String(id) || String(b.slug || '') === String(id))
        : null;

      if (fromProp?.data && Object.keys(fromProp.data).length > 0) {
        if (!cancelled) {
          const modelsResponse = editryReady ? await client.getPublicModels() : null;
          const resolvedVelikosti = modelsResponse
            ? await resolveVelikostDataFromRefs(fromProp.data?.velikosti, modelsResponse)
            : { labels: legacyVelikostiToLabels(fromProp.data?.velikosti), details: [] };

          let fallbackData = null;
          let bikeToHydrate = fromProp;

          if (editryReady && fromProp.id) {
            const models = modelsResponse?.data || [];
            const bikesModel = models.find((model) => model.slug === 'kola');
            if (bikesModel) {
              const detail = await loadModelItemDetail(bikesModel.id, fromProp.id);
              if (detail?.item) {
                bikeToHydrate = {
                  ...fromProp,
                  id: detail.item.id,
                  slug: detail.item.slug,
                  name: detail.item.name,
                  data: parseModelItemData(detail.item),
                };
              }
              if (detail?.fallbackData) {
                fallbackData = detail.fallbackData;
              }
            }
          }

          hydrateBikeState(bikeToHydrate, resolvedVelikosti.labels, resolvedVelikosti.details, fallbackData);
          setLoading(false);
        }
        return;
      }

      if (!editryReady) {
        if (!cancelled) {
          setBike(null);
          setLoading(false);
        }
        return;
      }

      try {
        const modelsResponse = await client.getPublicModels();
        const bikesModel = modelsResponse?.data?.find((model) => model.slug === 'kola');

        if (!bikesModel) {
          if (!cancelled) {
            setBike(null);
          }
          return;
        }

        let modelItem = null;
        let fallbackData = null;
        const numericId = Number(id);

        if (Number.isInteger(numericId) && numericId > 0) {
          const detail = await loadModelItemDetail(bikesModel.id, numericId);
          modelItem = detail?.item || null;
          fallbackData = detail?.fallbackData || null;
        }

        if (!modelItem) {
          let itemsResponse = await client.getPublicModelItems(bikesModel.id, language);
          let allItems = itemsResponse?.data || [];

          if (!allItems.length) {
            itemsResponse = await client.getPublicModelItems(bikesModel.id);
            allItems = itemsResponse?.data || [];
          }

          const listItem = allItems.find((item) => String(item.id) === String(id) || String(item.slug || '') === String(id)) || null;

          if (listItem?.id) {
            const detail = await loadModelItemDetail(bikesModel.id, listItem.id);
            modelItem = detail?.item || listItem;
            fallbackData = detail?.fallbackData || null;
          } else {
            modelItem = listItem;
          }
        }

        if (!cancelled && modelItem) {
          const normalizedBike = {
            id: modelItem.id,
            slug: modelItem.slug,
            name: modelItem.name,
            data: parseModelItemData(modelItem)
          };
          const resolvedVelikosti = await resolveVelikostDataFromRefs(normalizedBike.data?.velikosti, modelsResponse);
          hydrateBikeState(normalizedBike, resolvedVelikosti.labels, resolvedVelikosti.details, fallbackData);
        }
      } catch (error) {
        console.error('Chyba při načítání detailu kola:', error);
        if (!cancelled) {
          setBike(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadBike();

    return () => {
      cancelled = true;
    };
  }, [id, bikeData, client, editryReady, language, currentLocale]);

  // Funkce pro změnu barvy kola
  const handleColorChange = (index, colorList) => {
    const colors = Array.isArray(colorList) ? colorList : bike?.data?.barvy;
    setSelectedColorIndex(index);
    if (colors?.[index]?.obrazek) {
      setCurrentBikeImage(colors[index].obrazek);
    }
  };

  // Funkce pro galerii
  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setShowGallery(true);
  };
  
  const closeGallery = () => {
    setShowGallery(false);
  };
  
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
    );
  };
  
  /*
  const toggleCategory = (kategIndex) => {
    setOpenCategories(prevState => ({
      ...prevState,
      [kategIndex]: !prevState[kategIndex]
    }));
  };
  */
  
  const adjustLetterSpacing = () => {
    const box = boxRef.current;
    const text = textRef.current;
  
    if (box && text) {
      const boxWidth = box.offsetWidth;
      const textContent = text.textContent;
  
      const textWidthWithoutSpacing = Array.from(textContent)
        .map((char) => {
          const span = document.createElement("span");
          span.style.fontSize = window.getComputedStyle(text).fontSize;
          span.style.fontFamily = window.getComputedStyle(text).fontFamily;
          span.style.fontWeight = window.getComputedStyle(text).fontWeight;
          span.textContent = char;
          document.body.appendChild(span);
          const charWidth = span.offsetWidth;
          span.remove();
          return charWidth;
        })
        .reduce((acc, width) => acc + width, 0);
  
      if (textWidthWithoutSpacing < boxWidth && textContent.length > 1) {
        const extraSpace = boxWidth - textWidthWithoutSpacing;
        const letterSpacing = extraSpace / (textContent.length);
        text.style.letterSpacing = `${letterSpacing}px`;
  
        text.style.marginRight = `-${letterSpacing}px`;
      } else {
        text.style.letterSpacing = "0px";
        text.style.marginRight = "0px";
      }
    }
  };

  const handleScroll = (e) => {
    const hash = e.currentTarget.hash.substring(1);
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
      
  
  useEffect(() => {
    adjustLetterSpacing();

    window.addEventListener("resize", adjustLetterSpacing);

    return () => {
      window.removeEventListener("resize", adjustLetterSpacing);
    };
  }, []);

  const [touchStartX, setTouchStartX] = useState(0);
  const MIN_SWIPE_DISTANCE = 50;

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX - touchEndX;

    if (distance > MIN_SWIPE_DISTANCE) {
      nextImage();
    }

    if (distance < -MIN_SWIPE_DISTANCE) {
      prevImage();
    }
  };


  useEffect(() => {
    if (!bike?.data) {
      return;
    }

    const normalized = normalizeSpecifikaceList(bike.data.specifikace);
    const categories = normalized?.nazevKategorie || [];
    const openIndex = categories.findIndex((category) => category.rozbaleno === true);
    const initialIndex = openIndex >= 0 ? openIndex : -1;

    setOpenKategory(initialIndex);
    setSpecDropdownOpen(false);

    if (isRozbalovaciNabidkaEnabled(bike.data) && categories.length > 0) {
      const displayedIndex = categories[initialIndex] ? initialIndex : 0;
      const spec = categories[displayedIndex];
      setSelectedSpecIndex(displayedIndex);
      const matchedColors = filterColorsForSpec(bike.data.barvy, spec);
      setSelectedColorIndex(0);
      const specImage = resolveSpecImage(spec, matchedColors);
      if (specImage) {
        setCurrentBikeImage(specImage);
      }
    } else {
      setSelectedSpecIndex(initialIndex);
    }
  }, [bike, currentLocale]);

  useEffect(() => {
    if (!specDropdownOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (specDropdownRef.current && !specDropdownRef.current.contains(event.target)) {
        setSpecDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [specDropdownOpen]);




  if (loading) {
    return (
      <motion.div className="page bike-example" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <BikeExampleLoading />
      </motion.div>
    );
  }

  if (!bike) {
    return (
      <motion.div className="page bike-example" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <BikeExampleLoading />
      </motion.div>
    );
  }  

  const specifikaceList = normalizeSpecifikaceList(bike.data?.specifikace) || defaultSpecifikace;
  const specOptions = Array.isArray(specifikaceList.nazevKategorie) ? specifikaceList.nazevKategorie : [];
  const showRozbalovaciNabidka = isRozbalovaciNabidkaEnabled(bike.data) && specOptions.length > 0;
  const safeSpecIndex = specOptions[selectedSpecIndex] ? selectedSpecIndex : 0;
  const selectedSpec = specOptions[safeSpecIndex];
  const allColors = Array.isArray(bike.data?.barvy) ? bike.data.barvy : [];
  const visibleColors = showRozbalovaciNabidka
    ? resolveColorsForSpec(allColors, selectedSpec)
    : allColors;
  const displayPrice = showRozbalovaciNabidka
    ? (selectedSpec?.cena || bike.data?.cena)
    : bike.data?.cena;
  const dropdownOptions = specOptions.map((category, index) => ({
    name: toDisplayString(category.kategorie, currentLocale, `Specifikace ${index + 1}`),
    price: category.cena
  }));

  const handleSpecSelect = (index) => {
    setSelectedSpecIndex(index);
    setOpenKategory(index);
    setSpecDropdownOpen(false);
    setSelectedColorIndex(0);

    const spec = specOptions[index];
    const matchedColors = filterColorsForSpec(allColors, spec);
    const specImage = resolveSpecImage(spec, matchedColors);
    if (specImage) {
      setCurrentBikeImage(specImage);
    }
  };

  const geometryColumns = Array.isArray(velikostiDetaily) ? velikostiDetaily : [];
  const geometryRows = collectGeometryRows(geometryColumns, currentLocale);
  const sizeOptions = geometryColumns.length > 0
    ? geometryColumns
    : (Array.isArray(dostupneVelikosti) ? dostupneVelikosti : []).map((label) => ({
        id: label,
        displayName: label,
        podvelikost: ""
      }));

  return (
    <>
      <motion.div className="page bike-example" key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <div className="cesta cesta-bike-example">
          <NavLink to="/#kola" onClick={handleScroll}><button><span>{getLocalizedText('general.bikes_caps')}</span></button></NavLink>
          <NavLink to={"/kola/" + kategorie} ><button><span>{kategorie.toUpperCase() || "loading"}</span></button></NavLink>
          <button className="cesta-active"><span>{toDisplayString(bike.data?.nazev, currentLocale, "Nukeproof bike")}</span></button>
        </div>


        <h1>{toDisplayString(bike.data?.nazev, currentLocale, "Kolo")}</h1>
        <h2>{toDisplayString(bike.data?.kratkyPopis, currentLocale, "")}</h2>
        <div className="bike-example-blocker" data-bike-id={id}>
        <div ref={boxRef}>
             <div ref={textRef} />
        </div>
        <img src={currentBikeImage || bike.data?.obrazek+"?format=webp" || testbike} alt={toDisplayString(bike.data?.nazev, currentLocale, "Nukeproof bike")} />
        </div>

        <div className={`bike-example-size${showRozbalovaciNabidka ? " bike-example-size-has-dropdown" : ""}`}>
          {showRozbalovaciNabidka && (
            <RozbalovaciNabidka
              options={dropdownOptions}
              selectedIndex={safeSpecIndex}
              open={specDropdownOpen}
              onToggle={() => setSpecDropdownOpen((prev) => !prev)}
              onSelect={handleSpecSelect}
              dropdownRef={specDropdownRef}
            />
          )}
          <h3>{getLocalizedText('bike.available_colors')}</h3>
          <div className="bike-example-size-color">
            {(visibleColors && Array.isArray(visibleColors) && visibleColors.length > 0) ? 
              visibleColors.map((barva, index) => (
                <div 
                  key={`${barva.jmenoBarvy || barva.jmenobarvy || "barva"}-${index}`} 
                  className={index === selectedColorIndex ? "bike-example-size-color-active" : ""} 
                  onClick={() => handleColorChange(index, visibleColors)}
                >
                  <span style={{backgroundColor: barva.barva || "#000000"}} title={barva.jmenoBarvy || barva.jmenobarvy || "Barva"} />
                </div>
              )) : (
                <>
                  <div className="bike-example-size-color-active"><span style={{backgroundColor: "#e61c1c"}}/></div>
                  <div><span style={{backgroundColor: "#1cd1e6"}} /></div>
                  <div><span style={{backgroundColor: "#1ce672"}} /></div>
                </>
              )
            }
          </div>

          <h3>{getLocalizedText('bike.available_sizes')}</h3>
          <div className="bike-example-size-size">
            {sizeOptions.length > 0 ? 
              sizeOptions.map((sizeOption) => {
                const velikost = sizeOption.displayName;
                return (
                <div 
                  key={sizeOption.id || velikost} 
                  className={size === velikost ? "bike-example-size-size-active" : ""}
                  onClick={() => setSize(velikost)}
                >
                  <span className="bike-example-size-size-label">{velikost}</span>
                  {sizeOption.podvelikost ? (
                    <span className="bike-example-size-size-sub">{sizeOption.podvelikost}</span>
                  ) : null}
                </div>
              );
              }) : (
                <>
                  <div className={size === "S" ? "bike-example-size-size-active" : ""} onClick={() => setSize("S")}><span className="bike-example-size-size-label">S</span></div>
                  <div className={size === "M" ? "bike-example-size-size-active" : ""} onClick={() => setSize("M")}><span className="bike-example-size-size-label">M</span></div>
                  <div className={size === "L" ? "bike-example-size-size-active" : ""} onClick={() => setSize("L")}><span className="bike-example-size-size-label">L</span></div>
                  <div className={size === "XL" ? "bike-example-size-size-active" : ""} onClick={() => setSize("XL")}><span className="bike-example-size-size-label">XL</span></div>
                </>
              )
            }
          </div>
          <button className="bike-example-what-my-size" onClick={() => {setTableModal({show: true, id: bike.data?.velikostniTabulka?.id || bike.data?.velikostnitabulka?.id })}}>
            <AiOutlineQuestionCircle /> {getLocalizedText('general.whatismysize')}
          </button>

          {/* Cena kola z vybrane specifikace, jinak bike.data.cena */}
          {displayPrice && (
            <>
            <h3>{getLocalizedText('bike.price')}</h3>
            <div className="bike-price-value">
              <span className="price-amount">{formatPrice(displayPrice)}</span>
              <ExchangeRateTooltip />
            </div>
            </>
          )}

          <NavLink to="/prodejci"><button className="kolo-pruvodce-button"><span>{getLocalizedText('general.lookforprodejce')}</span></button></NavLink>

          {/* 
          {kategorie.toUpperCase() === "GRAVEL" && (
          <>
            <h3>{getLocalizedText('general.pruvodce_text_small')}</h3>
            <NavLink to="/pruvodce/gravel"><button className="kolo-pruvodce-button"><span>{getLocalizedText('general.pruvodce_button_small')}</span></button></NavLink>
          </>
          )}

          {kategorie.toUpperCase() === "ROAD" && (
          <>
            <h3>{getLocalizedText('general.pruvodce_text_small')}</h3>
            <NavLink to="/pruvodce/road"><button className="kolo-pruvodce-button"><span>{getLocalizedText('general.pruvodce_button_small')}</span></button></NavLink>
          </>
          )}
          */}
        </div>

        <h1>{getLocalizedText('bike.about_product')}</h1>
        <p>{toDisplayString(bike.data?.oproduktu || bike.data?.oProduktu, currentLocale, "Popis produktu není k dispozici")}</p>
      
        <div className="bike-example-gallery">
          {(galleryImages && Array.isArray(galleryImages) && galleryImages.length > 0) ? 
            galleryImages.map((image, index) => (
              <div key={index} onClick={() => openGallery(index)}>
                <img
                  src={`${image}?format=webp`}
                  alt={`Nukeproof bike gallery ${index + 1}`}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            )) : (
              <>
              </>
            )
          }
        </div>

        {bike.data?.video && (
          <div className="bike-example-video">
            <ReactPlayer url={bike.data?.video} controls={true} />
          </div>
        )}

        {vlastnosti && bike.data?.vlastnosti && Array.isArray(bike.data.vlastnosti) && bike.data.vlastnosti.length > 0 && (
          <>
            <h1>{getLocalizedText('bike.features')}</h1>
            <div className="bike-vlastnosti">
              {bike.data.vlastnosti.map((vlastnost, index) => (
                <div key={index}>
                    <img
                      src={vlastnost.obrazek+"?format=webp" || (theme === "dark" ? telowhite : teloblack)} 
                      alt={toDisplayString(vlastnost.nazev, currentLocale, "Nukeproof vlastnosti kola")}
                    />
                    <div className="vlastnost-text">
                      <h3>{toDisplayString(vlastnost.nazev, currentLocale, `Vlastnost ${index + 1}`)}</h3>
                      {toDisplayString(vlastnost.popis, currentLocale, "Bez popisu").length > 250 ? (
                      <>
                        <h4 className={vlastnostiExpanded === index ? "vlastnost-small vlastnosti-expanded" : "vlastnost-small vlastnosti-collapsed"}>{toDisplayString(vlastnost.popis, currentLocale, "Bez popisu")}</h4>
                        {vlastnostiExpanded === index ? 
                          <button onClick={() => {setVlastnostiExpanded(-1)}}><span>{getLocalizedText('bike.close')}</span></button> :
                          <button onClick={() => {setVlastnostiExpanded(index)}}><span>{getLocalizedText('bike.get_more')}</span></button>
                        }
                      </>
                      ) : (
                        <h4>{toDisplayString(vlastnost.popis, currentLocale, "Bez popisu")}</h4>
                      )}
                    </div>
                  {/*<span>
                  <button onClick={() => {
                    setModal(true);
                    setTitleModal(toDisplayString(vlastnost.nazev, currentLocale, `Vlastnost ${index + 1}`));
                    setTextModal(toDisplayString(vlastnost.popis, currentLocale, "Bez popisu"));
                    setImageModal(vlastnost.obrazek || (theme === "dark" ? telowhite : teloblack));
                  }}>
                    {getLocalizedText('bike.learn_more')}</span>
                  </button>
                  */}
                </div>
              ))}
            </div>
          </>
        )}




      <h1>{getLocalizedText('bike.specifications')}</h1>
      {/* 
        <div className="bike-example-specifications">
          {(specifikaceList.nazevKategorie && Array.isArray(specifikaceList.nazevKategorie)) ? 
            specifikaceList.nazevKategorie.map((kategorie, kategIndex) => (
              <React.Fragment key={kategIndex}>
                <div 
                  className="category-header" 
                  onClick={() => toggleCategory(kategIndex)}
                  style={{ 
                    cursor: 'pointer', 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '10px 10px 10px 0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, fontSize: '17px' }}>{toDisplayString(kategorie.kategorie, currentLocale, `Kategorie ${kategIndex + 1}`)}</h4>
                    <span style={{ 
                      fontSize: '15px', 
                      transform: openCategories[kategIndex] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      color: theme === "dark" ? '#4e4e4e' : '#333333',
                    }}>
                      ▼
                    </span>
                  </div>
  
                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                    {kategorie.cena && (
                      <span style={{ 
                        fontSize: '15px', 
                        fontWeight: 'bold',
                        marginLeft: '15px',
                        color: '#000000',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {formatPrice(kategorie.cena)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div 
                  style={{ 
                    display: openCategories[kategIndex] ? 'block' : 'none',
                    transition: 'all 0.3s ease',
                    padding: '10px 0'
                  }}
                >
                  {(kategorie.specifikace && Array.isArray(kategorie.specifikace)) ? 
                    kategorie.specifikace.map((spec, specIndex) => (
                      <div 
                        key={specIndex}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '5px 0',
                          borderBottom: '1px dotted rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <h5 style={{ 
                          margin: 0, 
                          fontSize: '14px',
                          flex: 1,
                          fontWeight: 'bold'
                        }}>
                          {translateSpecName(toDisplayString(spec.jmenoSpecifikace, currentLocale, `Specifikace ${specIndex + 1}`)) || `Specifikace ${specIndex + 1}`}
                        </h5>
                        <h6 style={{ 
                          margin: 0, 
                          fontSize: '14px',
                          textAlign: 'right',
                          color: theme === "dark" ? '#000000' : '#333333'
                        }}>
                          {toDisplayString(spec.hodnotaSpecifikace, currentLocale, "N/A")}
                        </h6>
                      </div>
                    )) : (
                      <p>Žádné specifikace nejsou k dispozici</p>
                    )
                  }
                </div>
              </React.Fragment>
            )) : (
              <div className="empty-specifications">
                <p>Specifikace nejsou k dispozici</p>
              </div>
            )
          }
          <h3>{getLocalizedText('bike.spec_table_bottom_text')}</h3>
        </div>
        */}

        <div className="bike-example-specifications-rework">
          <h3>{toDisplayString(bike.data?.nazev, currentLocale, "Kolo")}</h3>

          {specifikaceList.nazevKategorie.length > 1 ? (<>
              {specifikaceList.nazevKategorie.map((kategorie, kategIndex) => (
                <>
                  <button onClick={() => {showRozbalovaciNabidka ? handleSpecSelect(kategIndex) : setOpenKategory(kategIndex)}} className="category-menu-button">{kategorie.kategorie} <MdExpandMore className={openKategory === kategIndex ? "expanded" : ""}/></button>

                  <AnimatePresence mode="wait">
                  {(openKategory === kategIndex) && (
                    <motion.div key={kategIndex} className="spec-category padding" animate={{ height: "auto" }} initial={{ height: 0 }} exit={{ height: 0 }} transition={{ duration: 0.3 }}>
                      {kategorie.specifikace.map((spec, specIndex) => (
                        <div className="spec-row">
                          <h4>{translateSpecName(toDisplayString(spec.jmenoSpecifikace, currentLocale, `Specifikace ${specIndex + 1}`)) || `Specifikace ${specIndex + 1}`}</h4>
                          <h4>{toDisplayString(spec.hodnotaSpecifikace, currentLocale, "N/A")}</h4>
                          {/* <h4 className="empty-text">|</h4> */}
                        </div>
                      ))}
                    </motion.div>
                  )}
                  </AnimatePresence>
                </>
              ))}
          </>) : (<>
            {specifikaceList.nazevKategorie.map((kategorie, kategIndex) => (
              <div key={kategIndex} className="spec-category">
                {kategorie.specifikace.map((spec, specIndex) => (
                  <div className="spec-row">
                    <h4>{translateSpecName(toDisplayString(spec.jmenoSpecifikace, currentLocale, `Specifikace ${specIndex + 1}`)) || `Specifikace ${specIndex + 1}`}</h4>
                    <h4>{toDisplayString(spec.hodnotaSpecifikace, currentLocale, "N/A")}</h4>
                    {/* <h4 className="empty-text">|</h4> */}
                  </div>
                ))}
              </div>
            ))}
          </>)}

          <h4 className="bike-example-specifications-rework-underlinetext">{getLocalizedText('bike.spec_table_bottom_text')}</h4>
          <img src={specsBackground} alt="Specifikace kola pozadí - Nukeproof" />
        </div>



        {/*<NavLink to={"https://www.nukeproof-bikes.com/en_CZ/configurator"}><button className="undspecbutton"><span className="konfigurator-topbar">Konfigurátor</span></button></NavLink>*/}

        {bike.data?.banner && (
          <NavLink to={"/konfigurator"}>
          <div className="config-banner">
            <h1>{getLocalizedText('bike.configure_custom')}</h1>
            <button><span>{getLocalizedText('configurator.configure')}</span></button>
          </div>
          </NavLink>
        )}


        {/* 
        {bike.data?.geometrieObrazek && (
        <>
          <h1>{getLocalizedText('bike.geometry')}</h1>
          <div className="bike-example-geometrie-box">
          <div className="bike-example-geometrie">
            <img className="bike-example-geometrie-image" src={bike.data?.geometrieObrazek+"?format=webp"} alt={toDisplayString(bike.data?.nazev, currentLocale, "Geometrie kola")} />
          </div>
          </div>
          {bike.data?.geometriePdf && (<span className="download-pdf" onClick={() => {download()}}><FaRegFilePdf /> {getLocalizedText('bike.technical_info_download')}</span>)}
        </>
        )}
        */}

        <>
          <h1 id="geometry">{getLocalizedText('bike.geometry')}</h1>
          <div className="geometry-container">
            <div>
              <img src={bike.data?.geometrieobrazek ? bike.data.geometrieobrazek : "https://images.cyclingfactory.be/transform/eyJidWNrZXQiOiJiY2YtYjJiLWltYWdlcyIsImtleSI6IkRJU1NFTlRDQVJCT04yOV9nZW9faW1hZ2UxX2E0MzhhNjUyZmU5MWFlODMxNjMwYTJmYTEzM2Y5ZjIxYTU4NDFjODdfMzg0MHgucG5nIiwiZWRpdHMiOnsicmVzaXplIjp7IndpZHRoIjo4ODAsIndpdGhvdXRFbmxhcmdlbWVudCI6dHJ1ZX0sIndlYnAiOnsicXVhbGl0eSI6NjB9fX0="} alt="Geometrie kola" />
            </div>  
            <div>
              <table>
                <thead>
                  <tr>
                    <th><span>-</span></th>
                    <th className="useless"><span>-</span></th>
                    {geometryColumns.length > 0 ? (
                      geometryColumns.map((column) => (
                        <th key={column.id}>{column.displayName}</th>
                      ))
                    ) : (
                      <>
                        <th>NP1</th>
                        <th>NP2</th>
                        <th>NP3</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {geometryRows.length > 0 ? (
                    geometryRows.map((row, rowIndex) => (
                      <tr key={`${row.id || row.name}-${rowIndex}`}>
                        <td className="alignleft">{row.id || String.fromCharCode(65 + rowIndex)}</td>
                        <td className="alignleft useless">{row.name}</td>
                        {geometryColumns.map((column) => {
                          const rowEntry = findGeometryEntry(column.geometrie, row, currentLocale);
                          return (
                            <td key={`${column.id}-${row.id || row.name}`}>
                              {toDisplayString(rowEntry?.value, currentLocale, "-")}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td className="alignleft">A</td>
                        <td className="alignleft useless">Reach</td>
                        <td>439/445/451mm</td>
                        <td>459/465/471mm</td>
                        <td>479/485/491mm</td>
                      </tr>
                      <tr>
                        <td className="alignleft">B</td>
                        <td className="alignleft useless">Stack</td>
                        <td>1</td>
                        <td>2</td>
                        <td>3</td>
                      </tr>
                      <tr>
                        <td className="alignleft">C</td>
                        <td className="alignleft useless">Effective top tube</td>
                        <td>1</td>
                        <td>2</td>
                        <td>3</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>










        {(bike.data?.velikostniTabulka || bike.data?.velikostnitabulka) && (
          <button className="size-table-bike-button" onClick={() => {setTableModal({show: true, id: bike.data?.velikostniTabulka?.id || bike.data?.velikostnitabulka?.id })}}><label>{getLocalizedText('bike.size_table')}</label></button>
        )}

        <NavLink to="/prodejci">
        <div className="bike-example-store-banner">
            <div>
              <img src={bikedealer} alt="Nukeproof bike dealers" />
            </div>
            <span>{getLocalizedText('nav.dealers')}</span>
            <button><label  className="pointer">{getLocalizedText('general.show_button')}</label></button>
        </div>
        </NavLink>
      </motion.div>
      {showGallery && (
        <div className="gallery-modal">
          <div className="gallery-modal-content">
            <button className="close-button" onClick={closeGallery}>
              <FaTimes />
            </button>

            <div 
              className="gallery-image-container"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <img 
                src={galleryImages[currentImageIndex] + "?format=webp"} 
                alt={`Nukeproof bike gallery ${currentImageIndex + 1}`} 
              />
            </div>

            <button className="nav-button prev" onClick={prevImage}>
              <FaArrowLeft />
            </button>

            <button className="nav-button next" onClick={nextImage}>
              <FaArrowRight />
            </button>

            <div className="gallery-counter">
              <button className="nav-btn next" onClick={prevImage}><FaArrowLeft /></button>
              {currentImageIndex + 1} / {galleryImages.length}
              <button className="nav-btn prev" onClick={nextImage}><FaArrowRight /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
